'use client'

// frontend/src/components/brand/PointerGlow.tsx
//
// A soft cornflower light that follows the presenter's pointer, so a room can
// see where the presenter is going. Ported from h2r.
//
// It sits above the page's cards and below the header, the rail and every
// dialog, popover and toast, so nothing you can click is ever tinted. It is
// gated on a fine pointer and on `prefers-reduced-motion`, so a phone attaches
// no listeners at all. Position is written to CSS variables from one
// requestAnimationFrame, so React never re-renders on a mouse move.

import { useEffect, useRef } from 'react'

/** Cornflower #8AA2DF: the core under the pointer, the mid tone and the reach. */
const GLOW_CORE = 'rgba(138, 162, 223, 0.22)'
const GLOW_MID = 'rgba(138, 162, 223, 0.10)'
const RADIUS = 460

/**
 * Above the content (no z-index, or z-10 at most) and below the dropdown (50),
 * header (100), rail (200), modal (300 to 400), popover, tooltip and toast
 * layers of the PA z-index scale in tailwind.config.js.
 */
const GLOW_LAYER = 40

export function PointerGlow() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const node = ref.current
    if (!node) return

    let x = 0
    let y = 0
    let queued = false

    const paint = () => {
      queued = false
      node.style.setProperty('--glow-x', `${x}px`)
      node.style.setProperty('--glow-y', `${y}px`)
      node.style.opacity = '1'
    }

    const onMove = (event: MouseEvent) => {
      x = event.clientX
      y = event.clientY
      if (queued) return
      queued = true
      requestAnimationFrame(paint)
    }

    const onLeave = () => {
      node.style.opacity = '0'
    }

    document.addEventListener('mousemove', onMove, { passive: true })
    document.addEventListener('mouseleave', onLeave)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseleave', onLeave)
    }
  }, [])

  return (
    <div
      ref={ref}
      aria-hidden
      className='pointer-events-none fixed inset-0 transition-opacity duration-300'
      style={{
        zIndex: GLOW_LAYER,
        opacity: 0,
        background: `radial-gradient(${RADIUS}px circle at var(--glow-x, 50%) var(--glow-y, 50%), ${GLOW_CORE} 0%, ${GLOW_MID} 38%, transparent 68%)`,
      }}
    />
  )
}
